import { Component, EventEmitter, Input, Output } from '@angular/core'

import { destinationLetterBreakdown, destinationLetterNumbers } from '../../../data/utils/payment-destination.util'
import { PaymentDestination, PaymentRow } from '../../../data/interfaces/lease-ledger.interface'
import { PAYMENT_STATUS_ENUM } from '@modules/private/payment/data/enum/payment.enum'

@Component({
  selector: 'app-payment-drawer',
  templateUrl: './payment-drawer.component.html',
  styleUrls: ['./payment-drawer.component.scss'],
})
export class PaymentDrawerComponent {
  @Input() visible = false
  @Input() payment: PaymentRow | null = null
  @Input() leaseId = ''
  /** Arrendamientos activos del cliente: contexto de la caja de gemelos (U4). */
  @Input() activeLeases = 0
  //* Dos vías, como los modales: con `[visible]` de una vía el change detection re-empuja el valor
  //* viejo y el cajón se reabre.
  @Output() visibleChange = new EventEmitter<boolean>()
  @Output() apply = new EventEmitter<PaymentRow>()
  @Output() cancelApplication = new EventEmitter<{ applicationId: string; paymentId: string; n: number | null; letters: number[]; amount: number }>()
  @Output() adjust = new EventEmitter<PaymentRow>()

  //* El estatus en español, el mismo que la tabla de pagos: el cajón decía «APPLIED» junto a una fila «Integrado».
  statusLabel(status: string | null | undefined): string {
    return PAYMENT_STATUS_ENUM[status as keyof typeof PAYMENT_STATUS_ENUM] ?? status ?? '—'
  }
  /** Corregir la fecha del pago a la del depósito real. El diálogo vive en la pantalla. */
  @Output() changeDate = new EventEmitter<PaymentRow>()

  readonly conceptLabels: Record<string, string> = { interest: 'Interés', moratoriums: 'Mora', accessories: 'Accesorios', capital: 'Capital', inputIva: 'IVA' }

  /** Cómo se nombra el destino del dinero: una letra de la tabla, el enganche o el residual. El enganche
   *  y el residual no tienen número de letra, y llamarlos «letra 0» era describir algo que no existe.
   *  Cuando el número no se pudo resolver (`n` nulo, aplicación parcial en otro arrendamiento) se nombra
   *  el arrendamiento y nada más: por la misma razón. */
  destinationLabel(d: PaymentDestination): string {
    const donde = d.folio || 'este arrendamiento'
    if (d.applicationType === 'FIRST_PAYMENT') return `${donde} · pago inicial`
    if (d.applicationType === 'RESIDUAL_PAYMENT') return `${donde} · residual`
    //* B-039 · Todas las letras a las que se aplicó, no solo la primera.
    const letras = destinationLetterNumbers(d)
    if (!letras.length) return donde
    return `${donde} · ${letras.length > 1 ? 'letras' : 'letra'} ${letras.join(', ')}`
  }

  /** B-039 · Cuánto le tocó a cada letra, cuando el pago se repartió entre varias. */
  readonly letterBreakdown = destinationLetterBreakdown
  /** B-039 · Las letras de la aplicación, para que el diálogo de cancelar las nombre todas. */
  readonly letterNumbers = destinationLetterNumbers

  get liveApplications() {
    return (this.payment?.destinations ?? []).filter((a) => a.status !== 'CANCELLED')
  }

  get otherLeases(): number {
    return new Set(this.liveApplications.filter((a) => a.leaseId && a.leaseId !== this.leaseId).map((a) => a.leaseId)).size
  }

  conceptKeys(byConcept: Record<string, number> | undefined): string[] {
    return Object.keys(byConcept || {}).filter((k) => Math.abs((byConcept as Record<string, number>)[k]) > 0)
  }

  /**
   * Bitácora del pago: el registro, cada aplicación (y su cancelación) y lo que `auditTrail[]` guarda
   * (ajustes, confirmación de duplicado), en orden de fecha. Solo con `auditTrail[]` casi todos los pagos
   * dirían «Sin movimientos». El importe se pinta con `| currency` en la plantilla.
   */
  get bitacora(): { label: string; date: string; user?: string; reason?: string; amount?: number }[] {
    const p = this.payment
    if (!p) return []
    const items: { label: string; date: string; user?: string; reason?: string; amount?: number }[] = [{ label: 'Registrado', date: p.registeredOn, user: p.registeredBy }]
    for (const d of p.destinations ?? []) {
      items.push({ label: `Aplicado a ${this.destinationLabel(d)}`, date: d.appliedOn, user: d.appliedBy, amount: d.amount })
      if (d.status === 'CANCELLED' && d.cancelledAt) items.push({ label: `Aplicación a ${this.destinationLabel(d)} cancelada`, date: d.cancelledAt, reason: d.cancelReason })
    }
    for (const e of p.auditTrail ?? []) items.push({ label: e.action, date: e.date, user: e.user, reason: e.reason })
    return items.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  hide(): void {
    this.visible = false
    this.visibleChange.emit(false)
  }
}
